import { LocationScope, LocationVarInheritance, MergedLocationVar } from "@swivl-mono/types";
import { Select, SelectItem, isOptionGroup, isSelectOption } from "@swivl-mono/ui";
import React, { useEffect } from "react";
import { useAutosave } from "react-autosave";

import { WorksheetComponentLabel } from "../Label/WorksheetComponent.Label";
import "../WorksheetComponent/WorksheetComponent.scss";

interface Props {
  className: string;
  label?: string;
  variable: MergedLocationVar;
  options?: SelectItem[];
  isMultiSelect?: boolean;
  type: LocationScope;
  controlType: "input" | "textarea";
  isEditing: boolean;

  disableReverting?: boolean;
  saveVariable: (value: string) => void;
  customize(): void;
  deleteVariable(): void;
  isLoading: boolean;
  tooltip?: string;
}

export function WorksheetComponentDropdown(props: Props) {
  const { label, type, variable, isEditing, disableReverting, saveVariable, deleteVariable, isMultiSelect } = props;

  const getSelectedValues = (rawValue?: string | null): string[] => {
    if (!isMultiSelect) {
      if (!rawValue) {
        return [];
      }
      return [rawValue];
    }

    if (!rawValue) {
      return [];
    }

    return rawValue
      .split(",")
      .map((value) => value.trim())
      .filter((value) => value !== "");
  };

  const serializeSelectedValues = (values: string[]): string => {
    if (!isMultiSelect) {
      return values[0] || "";
    }

    return values.join(",");
  };

  const [selectedValues, setSelectedValues] = React.useState<string[]>(getSelectedValues(variable.value));

  useEffect(() => {
    setSelectedValues(getSelectedValues(variable.value));
  }, [isMultiSelect, variable.value]);

  const value = selectedValues[0] || "";
  const serializedValue = serializeSelectedValues(selectedValues);
  useAutosave({
    data: serializedValue,
    onSave: (nextSerializedValue) => {
      if (nextSerializedValue !== variable.value) {
        saveVariable(nextSerializedValue);
      }
    }
  });

  const textForOption =
    props.options
      ?.flatMap((option) => {
        if (isOptionGroup(option)) {
          return option.options;
        }

        return isSelectOption(option) ? [option] : [];
      })
      .filter((option) => (isMultiSelect ? selectedValues.includes(option.value) : option.value === value))
      .map((option) => option.label)
      .join(", ") || "Unset";

  return (
    <div
      className={`WorksheetComponent WorksheetComponentDropdown ${props.className} ${
        isEditing ? "isEditing" : ""
      } inheritance_${variable.inheritance} control_${props.controlType} `}
    >
      <WorksheetComponentLabel
        key={variable.inheritance + variable.key + variable.id}
        text={label}
        inheritance={variable.inheritance}
        isEditing={isEditing}
        type={type}
        canRevert={!disableReverting}
        isLoading={props.isLoading}
        deleteVariable={deleteVariable}
        tooltip={props.tooltip}
      />
      {isEditing ? (
        variable.inheritance === LocationVarInheritance.NONE ? (
          <Select
            options={props.options}
            loading={props.isLoading}
            hasSearchInDropdown
            isMultiSelect={isMultiSelect}
            selectedValues={selectedValues}
            onChange={setSelectedValues}
          />
        ) : (
          <div className="parentText" onClick={props.customize}>
            {textForOption}
            <div className="parentTextOverlay">Click to Customize</div>
          </div>
        )
      ) : (
        <div className="valueText">{textForOption}</div>
      )}
    </div>
  );
}
